import { useAtomValue } from 'jotai/index';
import { useEffect, useState } from 'react';
import { useAppState } from '~/app/contexts/app-context';
import { useMarketConfig } from '~/app/market-config';
import { LinkWithParams, ParamValue } from '~/app/models/configurator-options';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { state } from '~/storage';

const PATH_PARAM_REG_EX = /\/:(\w+)/g;
const LANGUAGE_REG_EX = /\{\{language\}\}/g;
const AUDICODE_REG_EX = /\{\{audiCode\}\}/g;
const CARLINE_REG_EX = /\{\{carline\}\}/g;
const CARLINE_ID_REG_EX = /\{\{carlineId\}\}/g;
const CARLINE_ID_HYPH_REG_EX = /\{\{hyphenCarlineId\}\}/g;
const MODEL_YEAR_REG_EX = /\{\{modelYear\}\}/g;
const TRIMLINE_EX = /\{\{trimline\}\}/g;
const FINANCE_PRODUCT_EX = /\{\{financeProd\}\}/g;

const getParameterReplacements = ({
	audicode,
	language,
	carline,
	carlineId,
	hyphenCarlineId,
	modelYear,
	selectedTrimlineName,
	financingProduct,
}: {
	audicode?: string;
	language?: string;
	carline?: string;
	carlineId?: string;
	hyphenCarlineId?: string;
	modelYear?: string;
	selectedTrimlineName?: string;
	financingProduct?: string;
}) => {
	return new Map<RegExp, string>([
		[AUDICODE_REG_EX, audicode!],
		[LANGUAGE_REG_EX, language!],
		[CARLINE_REG_EX, carline!],
		[CARLINE_ID_REG_EX, carlineId!],
		[CARLINE_ID_HYPH_REG_EX, hyphenCarlineId!],
		[MODEL_YEAR_REG_EX, modelYear!],
		[TRIMLINE_EX, selectedTrimlineName || ''],
		[FINANCE_PRODUCT_EX, financingProduct!],
	]);
};

const getParamWildCards = ({
	audicode,
	language,
	carline,
	carlineId,
	modelYear,
	selectedTrimlineName,
	financingProduct,
}: {
	audicode?: string;
	language?: string;
	carline?: string;
	carlineId?: string;
	modelYear?: number;
	selectedTrimlineName?: string;
	financingProduct?: string;
}) => {
	return new Map<string, ParamValue>([
		['LANG', { param: 'lg', value: language }],
		['AUDICODE', { param: 'audicode', value: audicode }],
		['CODE', { param: 'code', value: audicode }],
		['VAC', { param: 'vac', value: audicode }],
		['PCL', { param: 'pcl', value: `https%3A%2F%2Fwww.audi.de%3D${audicode}` }],
		['FILTER_CARLINE', { param: 'filter', value: `carline.${carline}` }],
		['CARLINE', { param: 'carline', value: carline }],
		['CARLINEID', { param: 'carlineid', value: carlineId }],
		['VCL', { param: 'vcl', value: carlineId }],
		['MODELYEAR', { param: 'model_year', value: modelYear?.toString() }],
		['VMY', { param: 'vmy', value: modelYear?.toString() }],
		['CONFNAME', { param: 'confname', value: carline }],
		['VMT', { param: 'vmt', value: carline }],
		['CONFDESC', { param: 'conf_desc', value: selectedTrimlineName }],
		['VMN', { param: 'vmn', value: selectedTrimlineName }],
		['CONFFINANCE', { param: 'conf_finance', value: financingProduct }],
		['VFT', { param: 'vft', value: financingProduct }],
	]);
};

const useBuildUrl = ({
	url,
	pathParams,
	queryParams,
	colonParam,
	onQueryParamCallback,
}: LinkWithParams): { url: string; isLoading: boolean } => {
	const { language, config } = useMarketConfig();
	const { catalog } = useConfigurator();
	const defaultProduct = useAtomValue(state.defaultFinancialProduct);
	const appState = useAppState();
	const audiCode = appState.audiCode?.data.id;
	const loading = appState.audiCode?.loading;
	const [paramsString, setParamsString] = useState<string>('');
	const [colonString, setColonString] = useState<string>('');
	const [parametrizedUrl, setParametrizedUrl] = useState<string>('');

	const mapQueryParams = (queryParam: ParamValue) => {
		return (
			getParamWildCards({
				audicode: audiCode,
				language,
				carline: catalog.carInfo?.carline,
				carlineId: config.carlineId,
				modelYear: catalog.carInfo?.carIdentifier.model.year,
				selectedTrimlineName: catalog.trimlines
					?.flatMap((t) => t.driveTrains)
					?.find((item) => item.status.selected)?.name,
				financingProduct: config.financingInput?.product ?? defaultProduct,
			}).get(queryParam.param) ?? queryParam
		);
	};

	useEffect(() => {
		if (url === '') return;
		if (!audiCode || !catalog) return;

		const dynamicParamas = {
			lang: language,
			carlineId: config.carlineId,
		};
		setParamsString(
			queryParams
				.map((queryParam) => mapQueryParams(queryParam))
				.map((paramValue) => {
					if (!onQueryParamCallback) return paramValue;
					return onQueryParamCallback(dynamicParamas, {
						param: paramValue.param,
						value: !paramValue.value ? undefined : `${paramValue.value}`,
					});
				})
				.filter(({ value }) => !!value)
				.map(({ param, value }) => `${param}=${value}`)
				.reduce(
					(accumulator, paramValue) =>
						accumulator === '' ? `?${accumulator}${paramValue}` : `${accumulator}&${paramValue}`,
					'',
				),
		);

		// unfortunately Italy uses a non-standard URL pattern, which is neither a parameter nor a path.
		// so far we can only supply one of those CRIMES
		setColonString(colonParam ? getColonString(colonParam, audiCode) : '');

		setParametrizedUrl(
			(url.match(PATH_PARAM_REG_EX) ?? [])
				.map((param) => param.slice(2, param.length))
				.reduce((accumulatedUrl, pathParam) => {
					if (pathParam === 'LANG') return accumulatedUrl.replace(`:${pathParam}`, language);
					const param = pathParams[pathParam];
					const replaceParam =
						typeof param === 'function' ? param(dynamicParamas) : (pathParams[pathParam] as string);

					return !pathParams[pathParam]
						? accumulatedUrl
						: accumulatedUrl.replace(`:${pathParam}`, replaceParam);
				}, url),
		);
	}, [JSON.stringify(catalog), audiCode]);

	return { url: `${parametrizedUrl}${colonString}${paramsString}`, isLoading: loading || !audiCode };
};

function getColonString(colonParam: ParamValue, audicode: string | undefined): string {
	switch (colonParam.param) {
		case 'CONF_CODE':
			return `:${audicode}`;
		default:
			return '';
	}
}

function getResolvedUrl(
	url: string,
	audicode?: string,
	language?: string,
	carline?: string,
	carlineId?: string,
	hyphenCarlineId?: string,
	modelYear?: string,
	financingProduct?: string,
	selectedTrimlineName?: string,
) {
	if (!url) return '';

	const mapper = getParameterReplacements({
		audicode,
		language,
		carline,
		carlineId,
		hyphenCarlineId,
		modelYear,
		financingProduct,
		selectedTrimlineName,
	});

	mapper.forEach((val, key) => {
		if (url.search(key) > -1) {
			url = url.replace(key, val);
		}
	});

	return url;
}

export { getResolvedUrl, useBuildUrl };
